;(() => {
  /**
   * 安全解析 JSON 文本为数组
   * @param {string} text - 原始 JSON 字符串
   * @returns {Array<Object>} 解析后的数组；失败返回 []
   */
  const parseJSON = (text) => {
    try {
      let obj = JSON.parse(text || '[]')
      if (typeof obj === 'string') {
        try {
          obj = JSON.parse(obj)
        } catch {
          // ignore
        }
      }
      return Array.isArray(obj) ? obj : []
    } catch {
      return []
    }
  }

  /**
   * 将价格转换为“分”的整数
   * 注意：变体 JSON 可能返回小数字符串或整数分
   * @param {number|string|null} val - 输入价格
   * @returns {number} 分
   */
  const toCents = (val) => {
    if (val == null) return 0
    if (typeof val === 'number' && Number.isInteger(val)) return val
    const n = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^0-9.\-]/g, ''))
    if (Number.isNaN(n)) return 0
    return n < 1000 ? Math.round(n * 100) : Math.round(n)
  }

  /**
   * 使用 Shopify.formatMoney（如可用）格式化分
   * @param {number} cents - 金额（分）
   * @param {string} moneyFormat - 店铺 money_format 模板
   * @returns {string} 格式化后的价格
   */
  const formatMoney = (cents, moneyFormat) => {
    const fmt = moneyFormat || window.shopMoneyFormat || '${{amount}}'
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, fmt)
    }
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const parts = (number / 100).toFixed(precision).split('.')
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands)
      const centsPart = parts[1]
      return precision ? `${dollars}${decimal}${centsPart}` : dollars
    }
    let value = ''
    if (/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/.test(fmt)) {
      value = formatWithDelimiters(cents, 0, '.', ',')
    } else if (/\{\{\s*amount_with_comma_separator\s*\}\}/.test(fmt)) {
      value = formatWithDelimiters(cents, 2, '.', ',')
    } else if (/\{\{\s*amount_no_decimals\s*\}\}/.test(fmt)) {
      value = formatWithDelimiters(cents, 0)
    } else {
      value = formatWithDelimiters(cents, 2)
    }
    return fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, value)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, value)
      .replace(/\{\{\s*amount\s*\}\}/, value)
  }

  // 全局选择汇总（用于价格联动与批量加购）
  window.CareUpsell = window.CareUpsell || (() => {
    const selected = new Map() // Map<blockId, { variantId, priceCents, compareCents }>
    return {
      set(blockId, payload) {
        if (payload && payload.variantId) selected.set(blockId, payload)
        else selected.delete(blockId)
      },
      clearSelection() {
        selected.clear()
        document.querySelectorAll('.care-upsell__radio:checked').forEach((el) => {
          el.checked = false
          el.dispatchEvent(new Event('change', { bubbles: true }))
        })
      },
      getSelectedItemsForCart() {
        return Array.from(selected.values()).map((v) => ({ id: String(v.variantId), quantity: 1 }))
      },
      getSelectedTotalPrice() {
        let totalCents = 0
        selected.forEach((v) => { totalCents += Number(v.priceCents || 0) })
        return totalCents / 100
      },
      getSelectedTotalOriginalPrice() {
        let totalCents = 0
        selected.forEach((v) => {
          const pc = Number(v.priceCents || 0)
          const cc = Number(v.compareCents || 0)
          totalCents += cc > pc ? cc : pc
        })
        return totalCents / 100
      },
    }
  })()

  // 变体请求缓存（按 id 存储）
  const variantCache = new Map() // Map<string, object>

  /**
   * 获取变体 JSON（内存缓存）
   * @param {number|string} variantId - 变体 ID
   * @returns {Promise<Object|null>} 变体对象；失败返回 null
   */
  const fetchVariant = async (variantId) => {
    const key = String(variantId) // 缓存键
    if (variantCache.has(key)) return variantCache.get(key)
    try {
      const res = await fetch(`/variants/${key}.json`, { credentials: 'same-origin' })
      const data = await res.json()
      // 兼容不同返回结构：
      // - Admin REST 为 {"variant": {...}}（官方文档）
      // - 部分前台/代理返回可能为 {"product_variant": {...}} 或直接是变体对象
      // 见：https://shopify.dev/docs/api/admin-rest/latest/resources/product-variant
      let variant = null // 解析后的变体对象
      if (data) {
        if (data.variant) {
          variant = data.variant
        } else if (data.product_variant) {
          variant = data.product_variant
        } else if (typeof data.id !== 'undefined' && (data.price != null || data.product_id != null)) {
          variant = data
        }
      }
      variantCache.set(key, Promise.resolve(variant))
      return variant
    } catch {
      return null
    }
  }

  /**
   * 估算变体是否可售
   * 优先使用 available；否则依据库存策略推断：
   * - 不跟踪库存（inventory_management 为空）=> 可售
   * - 跟踪库存：qty > 0 可售；qty <= 0 且 inventory_policy === 'continue' 可售
   * - 其余未知 => 默认可售
   * @param {?Object} variant
   * @returns {boolean}
   */
  const isVariantAvailable = (variant) => {
    if (!variant) return false
    if (typeof variant.available === 'boolean') return variant.available
    const invMgmt = variant.inventory_management
    const qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : undefined
    const policy = variant.inventory_policy
    if (!invMgmt) return true
    if (typeof qty === 'number') {
      if (qty > 0) return true
      if (policy === 'continue') return true
      return false
    }
    return true
  }

  /**
   * 构建单个 Care 方案卡片
   * @param {Object} option - JSON 中的加购项
   * @param {?Object} variant - Care 商品变体
   * @param {string} moneyFormat - 店铺 money_format 模板
   * @param {string} groupName - 单选框分组名
   * @returns {HTMLDivElement} 卡片元素
   */
  const buildCard = (option, variant, moneyFormat, groupName) => {
    const card = document.createElement('div')
    card.className = 'care-upsell__card'

    const disabled = !isVariantAvailable(variant) // 是否不可售
    if (disabled) card.classList.add('is-disabled')

    const radioId = `care-radio-${groupName}-${option.variant_id}` // 唯一 input id
    const radio = document.createElement('input')
    radio.type = 'radio'
    radio.className = 'care-upsell__radio'
    radio.name = groupName
    radio.id = radioId
    radio.value = option.variant_id
    radio.disabled = disabled
    radio.dataset.variantId = option.variant_id
    radio.dataset.priceCents = variant ? String(toCents(variant.price)) : '0'
    radio.dataset.compareCents = variant ? String(toCents(variant.compare_at_price)) : '0'
    if (option.default_choosed && !disabled) {
      radio.checked = true
      card.classList.add('is-selected')
    }

    const label = document.createElement('label')
    label.setAttribute('for', radioId)
    label.className = 'care-upsell__label'

    const top = document.createElement('div')
    top.className = 'care-upsell__top'

    const title = document.createElement('div')
    title.className = 'care-upsell__title'
    title.textContent = option.title || ''

    const price = document.createElement('div')
    price.className = 'care-upsell__price'
    if (variant) {
      const cents = toCents(variant.price) // 现价（分）
      const compareCents = toCents(variant.compare_at_price) // 划线价（分）
      const currentEl = document.createElement('span')
      currentEl.className = 'care-upsell__price-current'
      currentEl.textContent = formatMoney(cents, moneyFormat)
      price.appendChild(currentEl)
      if (compareCents > cents) {
        const compareEl = document.createElement('s')
        compareEl.className = 'care-upsell__price-compare'
        compareEl.textContent = formatMoney(compareCents, moneyFormat)
        price.appendChild(compareEl)
      }
    } else {
      price.textContent = ''
    }
    top.appendChild(title)
    top.appendChild(price)

    const desc = document.createElement('div')
    desc.className = 'care-upsell__desc'
    const span = document.createElement('span')
    span.textContent = option.desc || ''

    const link = document.createElement('a')
    link.href = String(option.btn_url || '').replace(/`/g, '').trim()
    link.target = '_blank'
    link.rel = 'noopener'
    link.className = 'care-upsell__link'
    link.textContent = option.btn_text || ''

    desc.appendChild(span)
    if (option.btn_text && option.btn_url) {
      desc.appendChild(document.createTextNode(' '))
      desc.appendChild(link)
    }

    label.appendChild(top)
    label.appendChild(desc)
    card.appendChild(radio)
    card.appendChild(label)

    // 单选变化时切换选中态 + 汇总数据
    radio.addEventListener('change', () => {
      const group = card.parentElement // 列表容器
      if (!group) return
      group.querySelectorAll('.care-upsell__card').forEach((c) => {
        const checked = c.querySelector('.care-upsell__radio')?.checked
        c.classList.toggle('is-selected', !!checked)
      })
      const blockEl = card.closest('.care-upsell-block')
      const blockId = blockEl ? `${blockEl.dataset.sectionId}-${blockEl.dataset.blockId}` : 'default'
      if (radio.checked) {
        window.CareUpsell.set(blockId, {
          variantId: radio.dataset.variantId,
          priceCents: Number(radio.dataset.priceCents || 0),
          compareCents: Number(radio.dataset.compareCents || 0),
        })
      } else {
        window.CareUpsell.set(blockId, null)
      }
      // 联动底部价格
      const vp = document.querySelector('variant-picker')
      if (vp && typeof vp.updateFixedFooterPrice === 'function') {
        vp.updateFixedFooterPrice()
      } else {
        document.dispatchEvent(new CustomEvent('care-upsell:selection-changed'))
      }
    })

    // 允许再次点击取消选择（Radio默认不支持）
    let wasChecked = false
    label.addEventListener('mousedown', () => {
      wasChecked = radio.checked
    })
    label.addEventListener('click', (e) => {
      if (wasChecked) {
        e.preventDefault()
        radio.checked = false
        radio.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
    return card
  }

  /**
   * 渲染单个 Care Upsell 区块实例
   * 当无数据或无匹配变体时隐藏整块
   * @param {HTMLElement} block - 区块根节点
   */
  const renderBlock = (block) => {
    const id = `${block.dataset.sectionId}-${block.dataset.blockId}` // 区块唯一 ID
    const moneyFormat = block.getAttribute('data-money-with-currency-format') || block.getAttribute('data-money-format') || '${{amount}}' // 金额格式模板
    const script = document.getElementById(`care-upsell-data-${id.replace(/:/g, '\\:')}`) // 数据节点
    if (!script) return

    const items = parseJSON(script.textContent) // Care 加购项
    const list = block.querySelector('.care-upsell__list') // 容器
    if (!Array.isArray(items) || !list || items.length === 0) {
      block.hidden = true
      block.setAttribute('aria-hidden', 'true')
      return
    }

    /**
     * 根据当前主商品变体刷新可见列表
     * @param {number|string} variantId - 当前变体 ID
     * @returns {Promise<void>} 异步完成
     */
    const updateForVariant = async (variantId) => {
      // 切换变体时先清空当前区块的已选状态与汇总
      window.CareUpsell.set(id, null)
      const visible = items.filter((it) => String(it.target_variant_id) === String(variantId)) // 匹配项
      list.innerHTML = ''
      if (visible.length === 0) {
        block.hidden = true
        block.setAttribute('aria-hidden', 'true')
        // 刷新底部价格，去掉上一个变体的 Care 价格
        document.dispatchEvent(new CustomEvent('care-upsell:selection-changed'))
        return
      }

      block.hidden = false
      block.removeAttribute('aria-hidden')

      const groupName = `care-upsell-choice-${id}` // 单选分组名
      // 并发获取所有 Care 变体
      const careVariants = await Promise.all(visible.map((it) => fetchVariant(it.variant_id)))
      visible.forEach((opt, idx) => {
        const card = buildCard(opt, careVariants[idx], moneyFormat, groupName)
        list.appendChild(card)
      })

      // 应用默认选中（匹配 default_choosed）
      const defaultItem = visible.find((v) => v.default_choosed)
      if (defaultItem) {
        const selector = `.care-upsell__radio[value="${defaultItem.variant_id}"]`
        const defRadio = list.querySelector(selector)
        if (defRadio && !defRadio.disabled) {
          defRadio.checked = true
          defRadio.dispatchEvent(new Event('change'))
        }
      } else {
        // 无默认选中：确保价格恢复为“仅主商品”（去掉上一个变体的 Care）
        document.dispatchEvent(new CustomEvent('care-upsell:selection-changed'))
      }
    }

    // 使用页面当前已选变体初始化
    const idInput = document.querySelector('.js-product-form input[name="id"]') // 隐藏输入，存储变体 ID
    const currentVariantId = idInput && idInput.value ? idInput.value : null
    if (currentVariantId) updateForVariant(currentVariantId)

    // 监听变体切换事件
    document.addEventListener('on:variant:change', (evt) => {
      const v = evt && evt.detail && evt.detail.variant ? evt.detail.variant.id : null
      if (v) updateForVariant(v)
    })
  }

  /**
   * 初始化页面中的所有 Care Upsell 区块
   */
  const init = () => {
    document.querySelectorAll('.care-upsell-block').forEach(renderBlock)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
